/**
 * Emergency Plan wizard — per-step Zod schemas.
 *
 * Lifted verbatim from
 * `apps/mobile/screens/wizards/WizardEmergencyPlanScreen.tsx`
 * (Phase 15b).
 *
 * Step order:
 *
 *   1. context   — project title, address, scope
 *   2. site      — postcode, nearest hospital, nearest fire station
 *   3. assembly  — assembly point + escape routes
 *   4. contacts  — site manager, principal contractor, project manager,
 *                  first aiders
 *   5. firstAid  — kit location + arrangements
 *   6. fire      — alarm test schedule + fire procedure
 *   7. spillage  — spill / hazardous materials procedure
 *   8. reporting — RIDDOR + near-miss reporting
 *   9. preview   — read-only summary (no validation)
 */
import { z } from "zod";
import { nearestHospitalField } from "./nearest-hospital";

// ─── Per-step schemas ──────────────────────────────────────────────────────

export const EmergencyPlanContextStepSchema = z.object({
  projectTitle: z.string().min(2, "Project title is required"),
  siteAddress: z.string().min(3, "Site address is required"),
  scopeDescription: z.string().optional().default(""),
});
export type EmergencyPlanContextStepData = z.infer<
  typeof EmergencyPlanContextStepSchema
>;

export const EmergencyPlanSiteStepSchema = z.object({
  sitePostcode: z.string().min(3, "Postcode is required"),
  nearestHospital: nearestHospitalField(
    "Note the nearest A&E (name + town is fine)",
  ),
  nearestFireStation: z.string().optional().default(""),
});
export type EmergencyPlanSiteStepData = z.infer<
  typeof EmergencyPlanSiteStepSchema
>;

export const EmergencyPlanAssemblyStepSchema = z.object({
  assemblyPoint: z.string().min(3, "Assembly point is required"),
  escapeRoutes: z
    .string()
    .min(10, "Describe the escape routes (at least one route)"),
});
export type EmergencyPlanAssemblyStepData = z.infer<
  typeof EmergencyPlanAssemblyStepSchema
>;

export const EmergencyPlanContactsStepSchema = z.object({
  siteManagerContact: z
    .string()
    .min(3, "Add the site manager (name + phone)"),
  principalContractorContact: z.string().optional().default(""),
  projectManagerContact: z.string().optional().default(""),
  firstAiderContacts: z
    .string()
    .min(3, "List at least one first aider (name + phone)"),
});
export type EmergencyPlanContactsStepData = z.infer<
  typeof EmergencyPlanContactsStepSchema
>;

export const EmergencyPlanFirstAidStepSchema = z.object({
  firstAidKitLocation: z
    .string()
    .min(3, "Where is the first aid kit kept on site?"),
  firstAidArrangements: z
    .string()
    .min(10, "Describe the first aid arrangements"),
});
export type EmergencyPlanFirstAidStepData = z.infer<
  typeof EmergencyPlanFirstAidStepSchema
>;

export const EmergencyPlanFireStepSchema = z.object({
  fireAlarmTestSchedule: z
    .string()
    .min(3, "How / when is the alarm tested?"),
  fireProcedure: z
    .string()
    .min(10, "Describe the fire evacuation procedure"),
});
export type EmergencyPlanFireStepData = z.infer<
  typeof EmergencyPlanFireStepSchema
>;

export const EmergencyPlanSpillageStepSchema = z.object({
  spillageProcedure: z
    .string()
    .min(10, "Describe the spill / hazardous materials procedure"),
});
export type EmergencyPlanSpillageStepData = z.infer<
  typeof EmergencyPlanSpillageStepSchema
>;

export const EmergencyPlanReportingStepSchema = z.object({
  riddorReporting: z
    .string()
    .min(10, "Describe when and how to report under RIDDOR"),
  nearMissReporting: z.string().min(5, "Describe near-miss reporting"),
});
export type EmergencyPlanReportingStepData = z.infer<
  typeof EmergencyPlanReportingStepSchema
>;

/** Preview is read-only — no validation. */
export const EmergencyPlanPreviewStepSchema = z.object({});
export type EmergencyPlanPreviewStepData = z.infer<
  typeof EmergencyPlanPreviewStepSchema
>;

// ─── Step ordering + composite ─────────────────────────────────────────────

export const EMERGENCY_PLAN_WIZARD_STEPS = [
  { id: "context", schema: EmergencyPlanContextStepSchema },
  { id: "site", schema: EmergencyPlanSiteStepSchema },
  { id: "assembly", schema: EmergencyPlanAssemblyStepSchema },
  { id: "contacts", schema: EmergencyPlanContactsStepSchema },
  { id: "firstAid", schema: EmergencyPlanFirstAidStepSchema },
  { id: "fire", schema: EmergencyPlanFireStepSchema },
  { id: "spillage", schema: EmergencyPlanSpillageStepSchema },
  { id: "reporting", schema: EmergencyPlanReportingStepSchema },
  { id: "preview", schema: EmergencyPlanPreviewStepSchema },
] as const;

export const EmergencyPlanWizardFullSchema = z.object({
  ...EmergencyPlanContextStepSchema.shape,
  ...EmergencyPlanSiteStepSchema.shape,
  ...EmergencyPlanAssemblyStepSchema.shape,
  ...EmergencyPlanContactsStepSchema.shape,
  ...EmergencyPlanFirstAidStepSchema.shape,
  ...EmergencyPlanFireStepSchema.shape,
  ...EmergencyPlanSpillageStepSchema.shape,
  ...EmergencyPlanReportingStepSchema.shape,
});
export type EmergencyPlanWizardData = z.infer<
  typeof EmergencyPlanWizardFullSchema
>;
